import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';

function monthLabel(iso) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}
function monthKey(iso) {
  return new Date(iso).toISOString().slice(0, 10);
}

function paceColor(pct) {
  if (pct == null) return 'var(--text-muted)';
  if (pct >= 100) return 'var(--green)';
  if (pct >= 75) return 'var(--yellow)';
  return 'var(--red)';
}

function GoalCell({ row, onSetGoal, saving }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(row.goalAmount ?? '');

  if (editing) {
    return (
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'center' }}>
        <input
          type="number"
          autoFocus
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { onSetGoal(row, value); setEditing(false); }
            if (e.key === 'Escape') setEditing(false);
          }}
          style={{
            width: 80, fontSize: 12, padding: '3px 6px', borderRadius: 4,
            border: '1px solid var(--border)', background: 'var(--surface-3, #1a1a1a)', color: 'var(--text)',
          }}
        />
        <button
          disabled={saving}
          onClick={() => { onSetGoal(row, value); setEditing(false); }}
          style={{ background: 'var(--yellow)', color: '#000', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
        >
          ✓
        </button>
      </div>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      style={{
        cursor: 'pointer', fontFamily: 'DM Mono, monospace',
        color: row.goalAmount != null ? 'var(--text)' : 'var(--text-muted)',
        borderBottom: '1px dashed var(--border)',
      }}
      title="Click to set goal"
    >
      {row.goalAmount != null ? `$${row.goalAmount.toLocaleString()}` : 'Set goal'}
    </span>
  );
}

export default function GoalPacing() {
  const { get, post } = useApi();
  const { currentClub } = useClub();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState({});

  // Default range: current month back 2 months, forward 1 — enough to see recent
  // pacing without pulling a full year on first load.
  const [range] = useState(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
  });

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    get(`/revenue/goal-pacing?club=${currentClub.id}&start=${range.start}&end=${range.end}`)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [currentClub.id, range.start, range.end]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function handleSetGoal(row, amount) {
    const parsed = parseFloat(amount);
    if (isNaN(parsed)) return;
    const key = `${row.category}|${row.month}`;
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await post('/revenue/goals', {
        locationId: currentClub.id, category: row.category,
        goalMonth: monthKey(row.month), goalAmount: parsed,
      });
      setData(prev => prev ? ({
        ...prev,
        rows: prev.rows.map(r =>
          r.category === row.category && r.month === row.month
            ? { ...r, goalAmount: parsed, pacePct: Math.round((r.grossRevenue / parsed) * 1000) / 10 }
            : r
        ),
      }) : prev);
    } catch (e) {
      console.error('Set goal failed', e);
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  }

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (error) return (
    <div style={{ padding: 24, color: 'var(--red)', textAlign: 'center' }}>
      Failed to load Goal Pacing — {error}
      <button onClick={load} style={{ marginLeft: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '4px 12px', borderRadius: 6, cursor: 'pointer' }}>
        Retry
      </button>
    </div>
  );

  const rows = data?.rows || [];
  const byMonth = rows.reduce((acc, r) => {
    (acc[r.month] = acc[r.month] || []).push(r);
    return acc;
  }, {});

  return (
    <div style={{ padding: '16px 0' }}>
      {data?.note && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16, fontStyle: 'italic' }}>
          {data.note}
        </div>
      )}

      {Object.keys(byMonth).sort().reverse().map(month => {
        const monthRows = byMonth[month];
        const totalActual = monthRows.reduce((s, r) => s + r.grossRevenue, 0);
        const totalGoal = monthRows.reduce((s, r) => s + (r.goalAmount || 0), 0);

        return (
          <div key={month} style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{monthLabel(month)}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                ${totalActual.toLocaleString(undefined, { minimumFractionDigits: 2 })} actual
                {totalGoal > 0 && ` / $${totalGoal.toLocaleString()} goal`}
              </span>
            </div>
            <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Category', 'Transactions', 'Gross Revenue', 'Goal', 'Pace'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Category' ? 'left' : 'center', color: 'var(--text-muted)', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthRows.map((r, i) => (
                    <tr key={r.category} style={{ borderBottom: i < monthRows.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{r.category}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'DM Mono, monospace' }}>{r.transactionCount}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'DM Mono, monospace', fontWeight: 700, color: r.grossRevenue < 0 ? 'var(--red)' : 'var(--text)' }}>
                        ${r.grossRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <GoalCell row={r} onSetGoal={handleSetGoal} saving={saving[`${r.category}|${r.month}`]} />
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'DM Mono, monospace', fontWeight: 700, color: paceColor(r.pacePct) }}>
                        {r.pacePct != null ? `${r.pacePct}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {rows.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 32, fontStyle: 'italic' }}>
          No revenue data for this period.
        </div>
      )}
    </div>
  );
}
